import { privacy } from "@redotech/schema-privacy";
import * as z from "zod";
import {
  BinPrioritySchema,
  PersistentStorageLocationTypeSchema,
  StorageLocationType,
} from "./storage-location";
import {
  CoordinateSchema,
  RackCellSchema,
  WallSchema,
  WarehouseCellSchema,
} from "./warehouse-cell";

export const BinCoordinateSchema = z.object({
  isSellable: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  x: z.number().meta({ privacy: privacy.nonPii }),
  y: z.number().meta({ privacy: privacy.nonPii }),
  type: PersistentStorageLocationTypeSchema.nullish().meta({
    privacy: privacy.nonPii,
  }),
  priority: BinPrioritySchema.nullish().meta({ privacy: privacy.nonPii }),
});
export type BinCoordinate = z.infer<typeof BinCoordinateSchema>;

/** Returns true if the storage location is pickable.
 * The bin must be mapped (present in binCoordinates). If mapped but has no type, it's pickable.
 * Only BIN type is explicitly pickable; all other types are not. */
export function isPickable(
  binCoordinate: BinCoordinate | null | undefined,
): boolean {
  if (binCoordinate == null) {
    return false;
  }
  return (
    binCoordinate.type == null || binCoordinate.type === StorageLocationType.BIN
  );
}

/**
 * Axis-aligned, cell-snapped rectangle. The unit is grid cells (not pixels) so
 * zone hit-tests collapse to integer arithmetic and resolve identically on
 * client and server.
 */
export const ZoneRectangleSchema = z.object({
  // `x` / `y` can be negative — the warehouse canvas origin sits in the middle
  // of the viewport, so most drawn rectangles will have negative coordinates.
  // The save-time grid translation will normalize them into the persisted
  // grid's frame in a follow-up.
  x: z.number().int().meta({ privacy: privacy.nonPii }),
  y: z.number().int().meta({ privacy: privacy.nonPii }),
  width: z.number().int().positive().meta({ privacy: privacy.nonPii }),
  height: z.number().int().positive().meta({ privacy: privacy.nonPii }),
});
export type ZoneRectangle = z.infer<typeof ZoneRectangleSchema>;

/**
 * A logical region of the warehouse used for zone-based picking.
 *
 * Zones are identified by their key in `WarehouseGrid.zones` (a stable id —
 * generate at create time, never reuse). `name` and `color` are display-only.
 *
 * Spatial extent is a list of axis-aligned cell rectangles. Multiple
 * rectangles per zone are allowed (so an L-shape is two rectangles). Zones
 * MUST NOT overlap — the builder enforces this at draw time by clipping the
 * older zone's rectangles when a new zone is drawn over them, so the invariant
 * is structural, not validated.
 *
 * The "default zone" — exactly one per layout once zones are introduced — has
 * an empty `rectangles` array and implicitly covers every cell not claimed by
 * an explicit zone. Marking it with `isDefault: true` lets the builder pin it
 * and prevent deletion.
 */
export const ZoneBinPatternSchema = z.object({
  pattern: z.string().meta({ privacy: privacy.nonPii }),
  enabled: z.boolean().default(true).meta({ privacy: privacy.nonPii }),
});
export type ZoneBinPattern = z.infer<typeof ZoneBinPatternSchema>;

export const ZoneSchema = z.object({
  name: z.string().min(1).meta({ privacy: privacy.nonPii }),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .meta({ privacy: privacy.nonPii }),
  rectangles: z
    .array(ZoneRectangleSchema.meta({ privacy: privacy.structured }))
    .meta({ privacy: privacy.structured }),
  isDefault: z.boolean().optional().meta({ privacy: privacy.nonPii }),
  binPatterns: z
    .array(ZoneBinPatternSchema.meta({ privacy: privacy.structured }))
    .optional()
    .meta({ privacy: privacy.structured }),
});
export type Zone = z.infer<typeof ZoneSchema>;

export const WarehouseGridSchema = z.object({
  width: z.number().int().positive().meta({ privacy: privacy.nonPii }),
  height: z.number().int().positive().meta({ privacy: privacy.nonPii }),
  grid: z
    .array(
      z
        .array(WarehouseCellSchema.meta({ privacy: privacy.structured }))
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
  racks: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      RackCellSchema.meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.untypedPayload }),
  walls: z
    .array(WallSchema.meta({ privacy: privacy.structured }))
    .meta({ privacy: privacy.structured }),
  binCoordinates: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      BinCoordinateSchema.meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.untypedPayload }),
  startVertex: CoordinateSchema.nullish().meta({ privacy: privacy.structured }),
  endVertex: CoordinateSchema.nullish().meta({ privacy: privacy.structured }),
  upBearingDegrees: z
    .number()
    .min(0)
    .lt(360)
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  /**
   * Zone definitions, keyed by stable zone id. Optional for backward compat:
   * a layout without `zones` is treated by the engine as a single implicit
   * default zone covering the whole warehouse.
   */
  zones: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      ZoneSchema.meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  /**
   * Ordered list of zone ids that drives the routing chain for hierarchical
   * zone picking — the tote is picked at the first zone, passed to the next,
   * and so on. Must be a permutation of `zones` keys when both are present.
   * The default zone is included like any other; conventionally pinned last
   * so bins outside any explicit zone are picked at the end of the wave.
   */
  zoneSequence: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
});
export type WarehouseGrid = z.infer<typeof WarehouseGridSchema>;
export interface IWarehouseGridSchema extends z.ZodType<WarehouseGrid> {}
